import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import { Toaster } from "sonner";
import { MotionConfig } from "framer-motion";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PremiumProvider } from "@/context/PremiumContext";
import PremiumGate from "@/components/PremiumGate";
import FantasyAvailabilityGate from "@/components/FantasyAvailabilityGate";
import DestinsAvailabilityGate from "@/components/DestinsAvailabilityGate";
import Layout from "@/components/Layout";
import "@/App.css";
import "@/components/PageExperience.css";

const AccountPrivacy = lazy(() => import("@/pages/AccountPrivacy"));
const Media = lazy(() => import("@/pages/Media"));
const MediaRedirect = lazy(() => import("@/pages/Media").then(m => ({default: m.MediaRedirect})));
const MatchPreviewPage = lazy(() => import("@/pages/MatchPreviewPage"));
const TV = lazy(() => import("@/pages/TV"));
const TVStudio = lazy(() => import("@/pages/TVStudio"));
const Home = lazy(() => import("@/pages/Home"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const Competitions = lazy(() => import("@/pages/Competitions"));
const CompetitionDetail = lazy(() => import("@/pages/CompetitionDetail"));
const MatchDetail = lazy(() => import("@/pages/MatchDetail"));
const Tirage = lazy(() => import("@/pages/Tirage"));
const Clubs = lazy(() => import("@/pages/Clubs"));
const ClubDetail = lazy(() => import("@/pages/ClubDetail"));
const PlayerDetail = lazy(() => import("@/pages/PlayerDetail"));
const MercatoCoach = lazy(() => import("@/pages/MercatoCoach"));
const Instagram = lazy(() => import("@/pages/Instagram"));
const InstagramClub = lazy(() => import("@/pages/InstagramClub"));
const InstagramSearch = lazy(() => import("@/pages/InstagramSearch"));
const Matches = lazy(() => import("@/pages/Matches"));

const Rivalries = lazy(() => import("@/pages/Rivalries"));
const ClubManagement = lazy(() => import("@/pages/ClubManagement"));
const Destins = lazy(() => import("@/pages/Destins"));
const Fantasy = lazy(() => import("@/pages/Fantasy"));
const Pronostics = lazy(() => import("@/pages/Pronostics"));
const CoachProfile = lazy(() => import("@/pages/CoachProfile"));
const CoachRanking = lazy(() => import("@/pages/CoachRanking"));
const Profile = lazy(() => import("@/pages/Profile"));
const Soutenir = lazy(() => import("@/pages/Soutenir"));
const Tickets = lazy(() => import("@/pages/Tickets"));
const Admin = lazy(() => import("@/pages/Admin"));
const UefaCoefficient = lazy(() => import("@/pages/UefaCoefficient"));
const PowerRanking = lazy(() => import("@/pages/PowerRanking"));

const PageLoader = () => (
  <div className="min-h-[55vh] flex items-center justify-center" role="status" aria-live="polite">
    <div className="text-center">
      <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-emerald-400/20 border-t-emerald-400" />
      <p className="mt-4 text-sm text-slate-400">Chargement de la page…</p>
    </div>
  </div>
);

const Protected = ({ roles, children }) => {
  const { user, authError, refresh, checking } = useAuth();
  if (user === null && authError) return <div role="status" className="mx-auto max-w-md px-6 py-20 text-center"><p className="text-slate-300">Impossible de vérifier ta connexion pour le moment.</p><button type="button" disabled={checking} onClick={refresh} className="mt-5 rounded-xl bg-emerald-400 px-5 py-3 font-bold text-slate-950 disabled:opacity-50">{checking ? "Vérification…" : "Réessayer"}</button></div>;
  if (user === null) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

const NotFound = () => (
  <main className="mx-auto flex min-h-[65vh] max-w-2xl items-center px-6 text-center">
    <div className="w-full rounded-3xl border border-white/10 bg-white/[0.035] p-8 sm:p-12">
      <p className="text-sm font-bold uppercase tracking-[0.25em] text-emerald-400">Erreur 404</p>
      <h1 className="mt-3 font-head text-3xl font-bold sm:text-4xl">Cette page n’existe pas</h1>
      <p className="mt-4 text-slate-400">Le lien est peut-être ancien. Toutes les fonctionnalités restent accessibles depuis l’accueil.</p>
      <Link to="/" className="mt-7 inline-flex rounded-xl bg-emerald-400 px-5 py-3 font-semibold text-slate-950 hover:bg-emerald-300">Retour à l’accueil</Link>
    </div>
  </main>
);

function App() {
  return (
    <AuthProvider>
      <PremiumProvider>
      <BrowserRouter>
        <Toaster theme="dark" position="top-right" richColors />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/medias" element={<Media />} />
              <Route path="/journal" element={<MediaRedirect tab="journal" />} />
              <Route path="/avant-match/:id" element={<MatchPreviewPage />} />
              <Route path="/tv" element={<TV />} />
              <Route path="/tv/studio" element={<Protected><PremiumGate feature="tv_studio"><TVStudio /></PremiumGate></Protected>} />
              <Route path="/tv/:id" element={<TV />} />
              <Route path="/competitions" element={<Competitions />} />
              <Route path="/competitions/:id" element={<CompetitionDetail />} />
              <Route path="/coefficient-uefa" element={<UefaCoefficient />} />
              <Route path="/clubs" element={<Clubs />} />
              <Route path="/indice-puissance" element={<PremiumGate feature="advanced_stats"><PowerRanking /></PremiumGate>} />
              <Route path="/clubs/:id" element={<ClubDetail />} />
              <Route path="/joueurs" element={<Navigate to="/clubs" replace />} />
              <Route path="/joueurs/:id" element={<PremiumGate feature="squad"><PlayerDetail /></PremiumGate>} />
              <Route path="/mercato" element={<Navigate to="/mercato-coach" replace />} />
              <Route path="/mercato-coach" element={<MercatoCoach />} />
              <Route path="/instagram" element={<PremiumGate feature="instagram"><Instagram /></PremiumGate>} />
              <Route path="/instagram/recherche" element={<Protected><PremiumGate feature="instagram"><InstagramSearch /></PremiumGate></Protected>} />
              <Route path="/instagram/clubs/:id" element={<Protected><PremiumGate feature="instagram"><InstagramClub /></PremiumGate></Protected>} />
              <Route path="/matchs" element={<Matches />} />
              <Route path="/matchs/:id" element={<MatchDetail />} />
              <Route path="/tirage/:id" element={<Tirage />} />
              <Route path="/actualites" element={<MediaRedirect tab="actualites" />} />
              <Route path="/rivalites" element={<PremiumGate feature="rivalries"><Rivalries /></PremiumGate>} />
              <Route path="/gestion-club" element={<Protected><ClubManagement /></Protected>} />
              <Route path="/mon-club" element={<Navigate to="/gestion-club" replace />} />
              <Route path="/mon-club/effectif" element={<Navigate to="/gestion-club?onglet=equipe" replace />} />
              <Route path="/destins" element={<DestinsAvailabilityGate><Destins /></DestinsAvailabilityGate>} />
              <Route path="/destins/coaches/:coachId" element={<DestinsAvailabilityGate><Destins /></DestinsAvailabilityGate>} />
              <Route path="/destins/clubs/:clubId" element={<DestinsAvailabilityGate><Destins /></DestinsAvailabilityGate>} />
              <Route path="/fantasy" element={<FantasyAvailabilityGate><Protected><PremiumGate feature="fantasy"><Fantasy /></PremiumGate></Protected></FantasyAvailabilityGate>} />
              <Route path="/pronostics" element={<Protected><Pronostics /></Protected>} />
              <Route path="/coaches/:id" element={<CoachProfile />} />
              <Route path="/classement-coachs" element={<CoachRanking />} />
              <Route path="/recompenses" element={<CoachRanking />} />
              <Route path="/soutenir" element={<Soutenir />} />
              <Route path="/confidentialite" element={<AccountPrivacy />} />
              <Route path="/conditions-premium" element={<Navigate to="/" replace />} />
              <Route path="/tickets" element={<Protected><Tickets /></Protected>} />
              <Route path="/tickets/new" element={<Protected><Tickets /></Protected>} />
              <Route path="/tickets/:id" element={<Protected><Tickets /></Protected>} />
              <Route path="/profil" element={<Protected><Profile /></Protected>} />
              <Route path="*" element={<NotFound />} />
            </Route>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/admin/*" element={<Protected roles={["admin", "support"]}><Admin /></Protected>} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </PremiumProvider>
    </AuthProvider>
  );
}

export class AppErrorBoundary extends React.Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(error, info) {
    console.error("[DayOne] Affichage interrompu", error, info.componentStack);
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <main className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-6" role="alert">
        <div className="max-w-md text-center">
          <p className="text-emerald-400 font-semibold mb-3">DayOne</p>
          <h1 className="text-2xl font-bold mb-4">Cette page n’a pas pu s’afficher</h1>
          <p className="text-slate-300 mb-6">Réessaie en rechargeant la page, ou retourne à l’accueil.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <button type="button" onClick={() => window.location.reload()} className="rounded-xl bg-emerald-400 text-slate-950 font-semibold px-5 py-3">Recharger la page</button>
            <a href="/" className="rounded-xl border border-slate-600 px-5 py-3">Retour à l’accueil</a>
          </div>
        </div>
      </main>
    );
  }
}

function SafeApp() { return <AppErrorBoundary><MotionConfig reducedMotion="user"><App /></MotionConfig></AppErrorBoundary>; }
export default SafeApp;

